/**
 * FILE record content is modeled in the resource form as a latin-1 "byte string":
 * each string code unit holds one raw byte of the file (produced by decoding the
 * base64 payload with `Buffer.toString("binary")`, and re-encoded on save with
 * `Buffer.from(value, "binary")`). That representation round-trips ANY file
 * byte-identically, so the stored bytes are never mutated by the Console — a
 * required guarantee for binary files and for text in encodings the Console does
 * not normalise.
 *
 * Monaco (the content preview/editor) treats its value as Unicode text, so feeding
 * it the raw byte string renders UTF-8 multi-byte characters as mojibake (e.g. an
 * em-dash "—", whose UTF-8 bytes are E2 80 94, shows as "â€""). These helpers
 * convert between the byte string and a UTF-8 text string AT THE EDITOR BOUNDARY
 * ONLY: the canonical form value stays the byte string, so an untouched file still
 * saves byte-identically, while valid-UTF-8 text content displays and edits as
 * UTF-8.
 */

const binaryStringToBytes = (binary: string): Uint8Array => {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i) & 0xff;
    }
    return bytes;
};

const bytesToBinaryString = (bytes: Uint8Array): string => {
    // Chunk to stay within the argument-count limit of String.fromCharCode.
    let out = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        out += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return out;
};

/**
 * Decode a latin-1 byte string as UTF-8 text, but ONLY when the bytes are valid
 * UTF-8. Returns null when the bytes are not valid UTF-8 (binary content, or a
 * non-UTF-8 text encoding such as latin-1/cp1252), so the caller falls back to the
 * raw byte string unchanged and never risks corrupting the content on display or
 * on a subsequent save.
 */
export const decodeBinaryStringAsUtf8 = (binary: string | undefined | null): string | null => {
    if (typeof binary !== "string" || binary.length === 0) {
        return null;
    }
    if (typeof TextDecoder === "undefined") {
        return null;
    }
    try {
        return new TextDecoder("utf-8", { fatal: true }).decode(binaryStringToBytes(binary));
    } catch {
        // Not valid UTF-8 -> keep the raw byte string.
        return null;
    }
};

/**
 * Decode a latin-1 byte string using an explicit stored encoding label — the
 * backend-detected FILE `content_encoding` (e.g. "windows-1252", "utf-16"). This
 * lets genuinely non-UTF-8 text files display as correct Unicode text in the
 * editor, instead of falling back to the raw byte string (mojibake) as
 * decodeBinaryStringAsUtf8 does for non-UTF-8 bytes.
 *
 * Returns null when:
 *  - no usable encoding label is given (caller falls back to the UTF-8-if-valid path);
 *  - the label is not a recognised WHATWG encoding, so `new TextDecoder(label)`
 *    throws a RangeError (charset-normalizer can emit codec names that are not
 *    valid TextDecoder labels) — the caller then falls back to the UTF-8 path;
 *  - decoding otherwise fails.
 *
 * `content_encoding` is only ever set by the backend for text content-types, so
 * this is not invoked for binary files. Decoding is non-fatal: legacy single-byte
 * encodings map every byte, and a best-effort render is preferred over mojibake.
 */
export const decodeBinaryStringWithEncoding = (
    binary: string | undefined | null,
    encoding: string | undefined | null
): string | null => {
    if (typeof binary !== "string" || binary.length === 0) {
        return null;
    }
    if (typeof encoding !== "string" || encoding.length === 0) {
        return null;
    }
    if (typeof TextDecoder === "undefined") {
        return null;
    }
    try {
        return new TextDecoder(encoding).decode(binaryStringToBytes(binary));
    } catch {
        // Unrecognised encoding label (RangeError) or decode failure -> let the
        // caller fall back to the UTF-8-if-valid path, then to the raw byte string.
        return null;
    }
};

/**
 * Encode UTF-8 text back to the latin-1 byte string used as the canonical form
 * value, so a save re-encodes it to the exact original bytes for unedited content
 * and to correct UTF-8 bytes for edited content.
 */
export const encodeUtf8AsBinaryString = (text: string): string => {
    if (typeof TextEncoder === "undefined") {
        return text;
    }
    return bytesToBinaryString(new TextEncoder().encode(text));
};
